import { useCallback, useEffect, useState } from 'react'
import { requestCalculationData, requestEcoSystemCalculator, requestTreatmentData } from 'api/site'
import { computeOverallKbaCriteria } from 'flow/siteProposal/pages/calculations/utils/computeOverallKbaCriteria'

const ALL_SPECIES_RESULTS_PER_PAGE = 10000

/**
 * Fetches species, ecosystem and treatment data and returns the concatenated
 * KBA criteria met across all triggers for the Assessment tab.
 */
export const useOverallKbaCriteria = (siteId, historyId, isReassessment) => {
  const [criteria, setCriteria] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const loadCriteria = useCallback(async () => {
    if (!siteId) {
      setCriteria([])
      return
    }

    setIsLoading(true)
    try {
      const [speciesResponse, ecoResponse, treatmentResponse] = await Promise.all([
        requestCalculationData(siteId, historyId, {
          resultsPerPage: ALL_SPECIES_RESULTS_PER_PAGE,
          currentPage: 1
        }),
        requestEcoSystemCalculator(siteId, historyId),
        requestTreatmentData(siteId, historyId)
      ])

      const species = Array.isArray(speciesResponse?.parsed) ? speciesResponse.parsed : []
      const eco = Array.isArray(ecoResponse) ? ecoResponse : []
      const treatment = treatmentResponse && typeof treatmentResponse === 'object' ? treatmentResponse : {}

      setCriteria(computeOverallKbaCriteria({
        species,
        eco,
        treatment,
        isReassessment
      }))
    } catch {
      setCriteria([])
    } finally {
      setIsLoading(false)
    }
  }, [siteId, historyId, isReassessment])

  useEffect(() => {
    loadCriteria()
  }, [loadCriteria])

  return { criteria, isLoading, refetch: loadCriteria }
}
